import React, { useEffect, useRef, useState } from 'react';
import { CopyButton } from '../debug/CopyButton';

/**
 * LatencyOverlayContent — inner content of the LATENCY debug tool.
 *
 * Polls window.__HELIX_MP_DIAGNOSTICS__ every 250ms and renders P1/P2/P6
 * phase means + P3+5 net one-way latency + clock sync info + a small
 * sparkline with red spike markers (>200ms threshold). When a spike
 * fires, a synthetic `[lag]` packet is injected into the tracer so it
 * appears in NetobsPacketLogPanel.
 *
 * Visibility is no longer controlled inside this component — it always
 * renders when mounted. DebugHUDHost decides when to mount it (driven by
 * DebugToolsContext + DebugLauncher).
 */
const POLL_INTERVAL_MS = 250;
const SPARKLINE_WIDTH = 160;
const SPARKLINE_HEIGHT = 28;
const SPARKLINE_HISTORY_SIZE = 60;   // 15 s at 250 ms
const SPIKE_THRESHOLD_MS = 200;

interface Snapshot {
  clockSync?: {
    ready: boolean;
    offsetMs: number;
    rttMs: number;
    oneWayLatencyMs: number;
    sampleCount: number;
    freshnessMs: number;
  };
  phaseTimings?: {
    sampleCount: number;
    rollingMean: { phase1Ms: number; phase2Ms: number };
    rollingMax: { phase1Ms: number; phase2Ms: number };
  };
  local?: {
    sessionId?: string | null;
  };
}

function injectLagSpikeLog(p3p5Ms: number, abilityId?: string): void {
  try {
    const tracer = (window as any).nm?.tracer;
    if (!tracer || typeof tracer.pushTrafficEvent !== 'function') return;
    const now = new Date();
    const iso = now.toISOString();
    const diag = (window as any).__HELIX_MP_DIAGNOSTICS__ as Snapshot | undefined;
    const sid = diag?.local?.sessionId ?? 'unknown';
    const type = `[lag] P3+5 spike ${Math.round(p3p5Ms)}ms abilityId=${abilityId ?? 'none'} sid=${sid}`;
    tracer.pushTrafficEvent({
      seq: tracer.networkEventSequence ?? 0,
      timestamp: iso,
      direction: 'client->server',
      type,
      bytes: 0,
      traceId: `lag_${now.getTime()}`,
    });
  } catch {
    // never crash the game for a diagnostic
  }
}

function buildSparklinePoints(values: number[]): string {
  if (values.length < 2) return '';
  const max = Math.max(...values, SPIKE_THRESHOLD_MS * 1.5, 10);
  const stepX = SPARKLINE_WIDTH / (values.length - 1);
  return values
    .map((v, i) => {
      const x = i * stepX;
      const y = SPARKLINE_HEIGHT - (v / max) * SPARKLINE_HEIGHT;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export const LatencyOverlayContent: React.FC = () => {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const historyRef = useRef<number[]>([]);
  const lastSpikeRef = useRef<number>(0);

  useEffect(() => {
    const tick = () => {
      if (typeof window !== 'undefined') {
        const s = (window as any).__HELIX_MP_DIAGNOSTICS__;
        if (s) {
          const nextSnap = { clockSync: s.clockSync, phaseTimings: s.phaseTimings };
          setSnap(nextSnap);

          // Update sparkline history
          const p3p5 = s.clockSync?.oneWayLatencyMs ?? 0;
          const hist = historyRef.current;
          hist.push(p3p5);
          if (hist.length > SPARKLINE_HISTORY_SIZE) hist.shift();

          // Spike detection — debounce 1 s so we don't flood the log
          const now = Date.now();
          if (p3p5 > SPIKE_THRESHOLD_MS && now - lastSpikeRef.current > 1000) {
            lastSpikeRef.current = now;
            // Try to grab the most-recent cast abilityId from the lifecycle hook
            // if available on window (set by AbilityLifecycleMonitor for debugging).
            const recentAbilityId = (window as any).__HELIX_LAST_CAST_ABILITY_ID as string | undefined;
            injectLagSpikeLog(p3p5, recentAbilityId);
          }
        }
      }
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const cs = snap?.clockSync;
  const pt = snap?.phaseTimings;
  const csReady = cs?.ready ?? false;
  const ptReady = (pt?.sampleCount ?? 0) > 0;
  const hist = historyRef.current;
  const points = buildSparklinePoints(hist);
  const maxVal = hist.length > 0 ? Math.max(...hist, SPIKE_THRESHOLD_MS * 1.5, 10) : 100;
  const spikeXPositions = hist
    .map((v, i) => (v > SPIKE_THRESHOLD_MS ? i : -1))
    .filter((i) => i !== -1)
    .map((i) => (i / Math.max(1, hist.length - 1)) * SPARKLINE_WIDTH);

  return (
    <div
      data-testid="latency-overlay"
      style={{
        color: '#ffd700',
        fontFamily: 'monospace',
        fontSize: 10,
        lineHeight: 1.35,
        padding: '4px 6px',
        userSelect: 'none',
      }}
    >
      <div style={{ color: '#7af', marginBottom: 2, letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>LATENCY (60-frame mean)</span>
        <CopyButton getText={() => [
          `LATENCY @ ${new Date().toISOString()}`,
          `P1 input:     ${ptReady ? `${pt!.rollingMean.phase1Ms.toFixed(2)} ms` : 'n/a'}`,
          `P2 predict:   ${ptReady ? `${pt!.rollingMean.phase2Ms.toFixed(2)} ms` : 'n/a'}`,
          `P3+5 net:     ${csReady ? `${cs!.oneWayLatencyMs.toFixed(0)} ms` : 'n/a'}`,
          `RTT:          ${csReady ? `${Math.round(cs!.rttMs)} ms` : '—'}`,
          `offset:       ${csReady ? `${cs!.offsetMs.toFixed(1)} ms` : '—'}`,
          `samples:      ${csReady ? cs!.sampleCount : 0}`,
        ].join('\n')} />
      </div>
      <div>P1 input: {ptReady ? `${pt!.rollingMean.phase1Ms.toFixed(2)} ms` : 'n/a'}</div>
      <div>P2 predict: {ptReady ? `${pt!.rollingMean.phase2Ms.toFixed(2)} ms` : 'n/a'}</div>
      <div>P3+5 net: {csReady ? `${cs!.oneWayLatencyMs.toFixed(0)} ms` : 'n/a'}</div>
      <div>P4 server: <span style={{ color: '#888' }}>n/a (deferred)</span></div>
      {/* Mini sparkline */}
      {hist.length > 1 && (
        <div style={{ marginTop: 6 }}>
          <svg width={SPARKLINE_WIDTH} height={SPARKLINE_HEIGHT} style={{ display: 'block' }}>
            {/* Grid line at threshold */}
            <line
              x1={0}
              y1={SPARKLINE_HEIGHT - (SPIKE_THRESHOLD_MS / maxVal) * SPARKLINE_HEIGHT}
              x2={SPARKLINE_WIDTH}
              y2={SPARKLINE_HEIGHT - (SPIKE_THRESHOLD_MS / maxVal) * SPARKLINE_HEIGHT}
              stroke="#444"
              strokeDasharray="2,2"
              strokeWidth={1}
            />
            {/* Sparkline polyline */}
            <polyline
              fill="none"
              stroke="#4ea3ff"
              strokeWidth={1.5}
              points={points}
            />
            {/* Spike markers */}
            {spikeXPositions.map((x, i) => (
              <line
                key={i}
                x1={x}
                y1={0}
                x2={x}
                y2={SPARKLINE_HEIGHT}
                stroke="#ff5f5f"
                strokeWidth={1.5}
                opacity={0.7}
              />
            ))}
          </svg>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#666', marginTop: 2 }}>
            <span>0</span>
            <span style={{ color: '#ff5f5f' }}>spike &gt;{SPIKE_THRESHOLD_MS}ms</span>
            <span>{Math.round(maxVal)}ms</span>
          </div>
        </div>
      )}

      <div style={{ color: '#7af', margin: '4px 0 2px', letterSpacing: '0.05em' }}>CLOCK SYNC</div>
      <div>RTT: {csReady ? `${Math.round(cs!.rttMs)} ms` : '—'}</div>
      <div>offset: {csReady ? `${cs!.offsetMs.toFixed(1)} ms` : '—'}</div>
      <div>samples: {csReady ? cs!.sampleCount : 0}</div>
    </div>
  );
};
